// English translations — overlays on top of the canonical Portuguese (pt-AO) dict.
// Missing keys fall back to Portuguese via the Proxy in `i18n.js`.
const en = {
    auth: {
        loginTitle: "NexusTel",
        loginTagline: "Every connection, managed with intelligence.",
        emailLabel: "Email address",
        emailPlaceholder: "name@angolateleccom.ao",
        passwordLabel: "Password",
        passwordPlaceholder: "Enter your password",
        submit: "Sign in to Platform",
        submitting: "Signing in…",
        invalidCredentials: "Invalid credentials. Please verify and try again.",
        showPassword: "Show password",
        hidePassword: "Hide password",
        footer: "© 2026 NexusTel — Angola Telecom",
        forgotPassword: "Forgot password?",
        sessionExpired: "Session expired. Please sign in again.",
    },
    nav: {
        dashboard: "Dashboard",
        services: "Services",
        customers: "Customers",
        fieldOps: "Field Operations",
        cx: "Customer Service",
        sales: "B2B Sales",
        revenue: "Revenue",
        analytics: "Analytics",
        integrations: "Integrations",
        admin: "Administration",
        users: "Users",
        conversations: "Conversations",
        kb: "Knowledge Base",
        profile: "Profile",
        logout: "Sign Out",
        section_operations: "Operations",
        section_intelligence: "Intelligence",
        section_platform: "Platform",
    },
    dashboard: {
        greetingMorning: "Good morning",
        greetingAfternoon: "Good afternoon",
        greetingEvening: "Good evening",
        kpi_activations_today: "Activations Today",
        kpi_open_tickets: "Open Tickets",
        kpi_csat: "Avg. CSAT",
        kpi_pipeline: "B2B Pipeline",
        quickActions: "Quick Actions",
        recentActivity: "Recent Activity",
        aiSummary: "AI Executive Summary",
        aiSummaryPlaceholder:
            "Available after configuring the Analytics module. The assistant will generate a daily briefing.",
        comingSoon: "Coming soon",
        viewAll: "View all",
        operationalStatus: "Systems operational",
        quick: {
            services: "Provision Service",
            servicesDesc: "Create a new activation or change",
            field: "Dispatch Technician",
            fieldDesc: "Assign a field job",
            tickets: "Open Ticket",
            ticketsDesc: "Log an incident or request",
            sales: "New Opportunity",
            salesDesc: "Start B2B qualification",
        },
    },
    users: {
        title: "User Management",
        subtitle: "Create and manage your organisation's accounts.",
        add: "Add User",
        searchPlaceholder: "Search by name or email…",
        filterRole: "All roles",
        filterStatus: "All statuses",
        active: "Active",
        inactive: "Inactive",
        col_user: "User",
        col_email: "Email",
        col_role: "Role",
        col_lastLogin: "Last sign-in",
        col_status: "Status",
        col_actions: "Actions",
        empty: "No users found.",
        neverLogged: "Never signed in",
        confirmDeactivate: "Are you sure you want to deactivate this user?",
        roleLabels: {
            super_admin: "Super Admin",
            operations_manager: "Operations Manager",
            field_technician: "Field Technician",
            customer_service_agent: "Customer Service Agent",
            sales_manager: "Sales Manager",
            sales_rep: "B2B Sales Rep",
            it_admin: "IT Admin",
            read_only_viewer: "Read-only Viewer",
        },
        form: {
            fullName: "Full name",
            email: "Corporate email",
            password: "Initial password",
            role: "Access role",
            phone: "Mobile (optional)",
            submit: "Create User",
            cancel: "Cancel",
        },
    },
    profile: {
        title: "My Profile",
        subtitle: "Manage your personal information and preferences.",
        personalInfo: "Personal Information",
        changePassword: "Change Password",
        currentPassword: "Current password",
        newPassword: "New password",
        confirmNew: "Confirm new password",
        savePassword: "Update password",
        saveProfile: "Save changes",
        savedOk: "Changes saved successfully.",
        passwordOk: "Password updated successfully.",
        passwordMismatch: "Passwords do not match.",
        language: "Interface language",
        languageHelp: "Affects navigation, dashboard and admin labels. Module-specific copy remains in Portuguese.",
        portuguese: "Portuguese (pt-AO)",
        english: "English",
    },
    notifications: {
        title: "Notifications",
        markAllRead: "Mark all as read",
        empty: "You're all caught up.",
        today: "Today",
        yesterday: "Yesterday",
        thisWeek: "This week",
        older: "Older",
    },
    common: {
        save: "Save",
        cancel: "Cancel",
        confirm: "Confirm",
        delete: "Delete",
        edit: "Edit",
        close: "Close",
        loading: "Loading…",
        searching: "Searching…",
        of: "of",
        page: "Page",
        none: "—",
        actions: "Actions",
        description: "Description",
        assignedTo: "Assigned to",
        assigned: "Assigned",
        service: "Service",
        session: "Session",
        sessionActive: "Session active",
        waiting: "Waiting",
        info: "Info",
        noComments: "No comments yet.",
        noServices: "No services found.",
        noDocuments: "No documents.",
        noTickets: "No tickets.",
        noLinkedTickets: "No linked tickets.",
        noJobs: "No jobs",
        noDeals: "No deals.",
        noNotes: "No notes. Click the button above to generate with AI.",
        serviceOrder: "Service order",
        lastSync: "Last Sync",
        rejectionReason: "Rejection reason",
        rejectOrder: "Reject order",
        chartActivations: "Activations",
        chartCsat: "Avg. CSAT",
        completed: "Completed",
        switchLangToEn: "Switch to English",
        switchLangToPt: "Switch to Portuguese",
        pwaInstallHint: "Quick access and offline mode for the field.",
        csatChurnHint: "Customers with recent 1-2★ ratings — high churn risk.",
        idpRepublic: "REPÚBLICA DE ANGOLA",
        idpBiPlaceholder: "Número de BI: ███-██████-██-█",
        idpExtractionTitle: "Intelligent Extraction",
        idpLoading: "⠿ waiting…",
        fieldOpsAsk: "Ask anything about field operations…",
    },
    placeholder: {
        title: "Module under construction",
        body: "This module will be activated in upcoming iterations.",
        eta: "Available in future sessions",
    },
    topBar: {
        search: "Search…",
        notifications: "Notifications",
        themeToggle: "Toggle theme",
    },
    services: {
        title: "Services & Provisioning",
        subtitle: "Activation pipeline and SLA — Angola Telecom.",
        new: "New Service Order",
        kpiActivated: "Activations Today",
        kpiPendingApproval: "Pending Approval",
        kpiSlaAtRisk: "SLA at Risk",
        kpiResolvedMonth: "Total Active",
        col_order: "Order",
        col_customer: "Customer",
        col_service: "Service",
        col_status: "Status",
        col_priority: "Priority",
        col_sla: "SLA",
        col_assignee: "Assignee",
        col_created: "Created",
        empty: "No orders found.",
        backToList: "Back to list",
        approve: "Approve",
        reject: "Reject",
        activate: "Activate",
        startProvisioning: "Start Provisioning",
        cancel: "Cancel",
        assign: "Assign",
        workflow: "Workflow",
        documentsPanel: "Documents",
        linkedTickets: "Linked tickets",
        notes: "Notes",
        slaBreached: "SLA breached",
        slaInTime: "Within SLA",
        status_draft: "Draft",
        status_pending_docs: "Documents pending",
        status_pending_approval: "Pending approval",
        status_approved: "Approved",
        status_in_provisioning: "Provisioning",
        status_active: "Active",
        status_rejected: "Rejected",
        status_cancelled: "Cancelled",
        priority_low: "Low",
        priority_medium: "Medium",
        priority_high: "High",
        priority_critical: "Critical",
        step_pending: "Pending",
        step_in_progress: "In progress",
        step_completed: "Completed",
        step_skipped: "Skipped",
        step_failed: "Failed",
    },
    wizard: {
        step1: "Customer",
        step2: "Documents",
        step3: "Service",
        step4: "Review",
        step5: "Confirmation",
        step1Title: "Customer Details",
        step1Sub: "Search by name, phone or ID. Or create a new customer.",
        step2Title: "Document Verification",
        step2Sub: "Upload customer documents for AI extraction.",
        step3Title: "Service Selection",
        step3Sub: "Choose the service to provision.",
        step4Title: "Final Review",
        step4Sub: "Confirm details before creating the order.",
        step5Title: "Order Created Successfully",
        step5Sub: "The order has been added to the provisioning pipeline.",
        existingCustomer: "Existing customer",
        newCustomer: "New customer",
        next: "Continue",
        back: "Back",
        finish: "Create Order",
        searchCustomer: "Search customer…",
        uploadDoc: "Upload Document",
        chooseDocType: "Document type",
        extractAi: "Extract with AI",
        timerLabel: "Session time",
        viewOrder: "View Order",
        backToServices: "Back to Services",
        documentReady: "Ready for review",
        documentProcessing: "Processing…",
        documentVerified: "Verified",
        priorityLabel: "Priority",
        notesLabel: "Notes (optional)",
    },
    customers: {
        title: "Customer Directory",
        subtitle: "Unified customer database — Angola Telecom.",
        new: "New Customer",
        types: { individual: "Individual", enterprise: "Enterprise", government: "Government" },
        statuses: { prospect: "Prospect", active: "Active", suspended: "Suspended", churned: "Churned" },
        filterType: "All types",
        filterStatus: "All statuses",
        col_name: "Name",
        col_type: "Type",
        col_phone: "Phone",
        col_lifetime: "LTV (AOA)",
        col_status: "Status",
        col_risk: "Churn Risk",
        empty: "No customers found.",
        tabs: { info: "Info", services: "Services", documents: "Documents", tickets: "Tickets", history: "History" },
        churnLow: "Low",
        churnMed: "Medium",
        churnHigh: "High",
    },
    tickets: {
        title: "Ticket Queue",
        subtitle: "Centralised customer service management.",
        new: "New Ticket",
        statuses: {
            aberto: "Open",
            em_progresso: "In Progress",
            pendente_cliente: "Pending Customer",
            resolvido: "Resolved",
            fechado: "Closed",
        },
        categories: {
            provisioning: "Provisioning",
            billing: "Billing",
            technical: "Technical",
            complaint: "Complaint",
            general: "General",
        },
        priority_low: "Low",
        priority_medium: "Medium",
        priority_high: "High",
        priority_critical: "Critical",
        view_kanban: "Kanban",
        view_table: "Table",
        filterAll: "All",
        allCategories: "All categories",
        allPriorities: "All priorities",
        addComment: "Add comment…",
        publishComment: "Publish",
        internalNote: "Internal note",
        publicReply: "Customer reply",
        timeline: "Timeline",
        slaRemaining: "SLA remaining",
        slaBreached: "SLA breached",
        backToList: "Back to queue",
        info: "Info",
        empty: "No tickets in this column.",
    },
    errors: {
        generic: "Something went wrong. Please try again.",
        network: "Network error. Check your connection.",
        unauthorised: "You don't have permission for this action.",
        forbidden: "You don't have permission for this operation.",
        notFound: "Resource not found.",
    },
    conversations: {
        title: "Conversations",
        subtitle: "Omnichannel inbox — Angola Telecom.",
        inbox: "Inbox",
        searchPlaceholder: "Search messages, customer…",
        filterAll: "All channels",
        filterStatus: "All statuses",
        channels: {
            web_chat: "Web Chat",
            whatsapp: "WhatsApp",
            email: "Email",
            voice: "Voice",
        },
        statuses: {
            active: "Active",
            waiting: "Waiting",
            escalated: "Escalated",
            closed: "Closed",
        },
        empty: "No conversations found.",
        emptyThread: "Select a conversation to view the history.",
        composerPlaceholder: "Write your reply…",
        send: "Send",
        sendInternal: "Internal note",
        triage: "AI Analysis",
        triageRun: "Classify with AI",
        triageUrgency: "Urgency",
        triageIntent: "Intent",
        triageSentiment: "Sentiment",
        triageSummary: "Summary",
        triageActions: "Suggested actions",
        triageTags: "Suggested KB tags",
        triageEmpty: "Click 'Classify with AI' to analyse the conversation.",
        draft: "AI Draft Reply",
        draftGenerate: "Generate with AI",
        draftRegenerate: "Regenerate",
        draftInsert: "Insert into editor",
        draftTone: "Tone",
        toneProfissional: "Professional",
        toneEmpatico: "Empathetic",
        toneFormal: "Formal",
        escalate: "Escalate to human",
        escalated: "Conversation escalated",
        close: "Close conversation",
        closed: "Conversation closed",
        csat: "Customer rating",
        urgency: {
            baixa: "Low",
            media: "Medium",
            alta: "High",
            critica: "Critical",
        },
        intent: {
            faturacao: "Billing",
            suporte_tecnico: "Technical Support",
            provisionamento: "Provisioning",
            reclamacao: "Complaint",
            informacao: "Information",
            outro: "Other",
        },
        sentiment: {
            muito_negativo: "Very Negative",
            negativo: "Negative",
            neutro: "Neutral",
            positivo: "Positive",
            muito_positivo: "Very Positive",
        },
        kpiOpen: "Open Conversations",
        kpiEscalated: "Escalated",
        kpiSentiment: "Avg. Sentiment",
        kpiCsat: "Avg. CSAT",
    },
    kb: {
        title: "Knowledge Base",
        subtitle: "Articles and procedures — Angola Telecom.",
        new: "New Article",
        searchPlaceholder: "Search articles…",
        filterAll: "All categories",
        categories: {
            faturacao: "Billing",
            instalacao: "Installation",
            suporte_tecnico: "Technical Support",
            comercial: "Commercial",
            geral: "General",
        },
        views: "views",
        helpful: "Helpful",
        empty: "No articles match this search.",
        readMore: "Read more",
        backToList: "Back to list",
    },
    sales: {
        title: "B2B Sales",
        subtitle: "Enterprise pipeline — Angola Telecom.",
        kpiPipeline: "Total Pipeline (AOA)",
        kpiLeads: "Leads This Month",
        kpiConversion: "Conversion Rate",
        kpiProposals: "Proposals Sent",
        funnelTitle: "Sales Funnel",
        dealsTitle: "Active Deals",
        newDeal: "New Deal",
        copilot: "Open Co-Pilot",
        viewProposals: "View Proposals",
        viewPipeline: "View Pipeline",
        col_deal: "Deal",
        col_company: "Company",
        col_stage: "Stage",
        col_value: "Value",
        col_owner: "Owner",
        col_close: "Expected Close",
        col_days: "Days in Stage",
        stages: {
            lead: "Lead",
            qualificado: "Qualified",
            proposta: "Proposal",
            negociacao: "Negotiation",
            fechado_ganho: "Closed (Won)",
            fechado_perdido: "Closed (Lost)",
        },
    },
    copilot: {
        title: "AI Sales Co-Pilot",
        subtitle: "Discovery, recommendation and proposal — in real time.",
        contextTitle: "Deal Context",
        companyLabel: "Customer Company",
        problemLabel: "Customer Problem",
        problemPlaceholder: "Describe the customer's challenge in detail…",
        startBtn: "Start Co-Pilot",
        chatPlaceholder: "Write the customer's reply or add context…",
        send: "Send",
        recommendationsTitle: "Real-Time Recommendations",
        recommendationsEmpty: "Recommendations will appear here as the conversation progresses.",
        generateProposal: "Generate Proposal Now",
        generatingProposal: "AI is drafting your proposal…",
        proposalReady: "Proposal generated successfully!",
        downloadPdf: "Download PDF",
        sendEmail: "Send by Email",
        viewProposal: "View Proposal",
        thinking: "Thinking…",
        analyzingNeeds: "Analysing needs…",
        productsIdentified: "Products identified",
        readyForProposal: "Ready to generate proposal!",
        aiCopilot: "AI Co-Pilot",
        agent: "Sales Rep",
    },
    proposals: {
        title: "Proposals",
        subtitle: "Commercial proposal management.",
        col_number: "Number",
        col_customer: "Customer",
        col_total: "Total",
        col_status: "Status",
        col_created: "Created",
        statuses: {
            draft: "Draft",
            sent: "Sent",
            viewed: "Viewed",
            accepted: "Accepted",
            rejected: "Rejected",
        },
        downloadPdf: "PDF",
        empty: "No proposals to display.",
    },
    analytics: {
        title: "Executive Analytics",
        subtitle: "Operational intelligence and AI narrative — Angola Telecom.",
        execSummary: "AI Executive Summary",
        execSummaryEmpty: "No summary generated yet. Click below to create the first one.",
        refresh: "Refresh Summary",
        generating: "AI is analysing this week's data…",
        period: "Period",
        periodDay: "Last day",
        periodWeek: "Last week",
        periodMonth: "Last month",
        headline: "Headline",
        highlights: "Highlights",
        risks: "Risks to Watch",
        keyMetrics: "Key Metrics",
        trends: "Trends (180 days)",
        chartActivations: "Daily Activations",
        chartCSAT: "Avg. CSAT",
        chartSLA: "Field SLA Compliance (%)",
        chartPipeline: "B2B Pipeline (USD)",
    },
    revenue: {
        title: "Revenue Assurance",
        subtitle: "Anomaly, fraud and revenue-leak detection.",
        atRisk: "At Risk (Open)",
        openAnomalies: "Open Anomalies",
        totalAnomalies: "Total Anomalies",
        activeAnomalies: "active anomalies",
        col_type: "Type",
        col_severity: "Severity",
        col_amount: "At Risk",
        col_status: "Status",
        col_description: "Description",
        col_customer: "Customer",
        col_actions: "Actions",
        types: {
            unbilled_service: "Unbilled service",
            billing_discrepancy: "Billing discrepancy",
            fraud_pattern: "Fraud pattern",
            unusual_usage: "Unusual usage",
            subscription_abuse: "Subscription abuse",
        },
        severities: {
            low: "Low",
            medium: "Medium",
            high: "High",
            critical: "Critical",
        },
        statuses: {
            open: "Open",
            under_investigation: "Under investigation",
            resolved: "Resolved",
            dismissed: "Dismissed",
        },
        filterSeverity: "All severities",
        filterStatus: "All statuses",
        filterType: "All types",
        empty: "No anomalies detected.",
        generateNotes: "AI Notes",
        markInvestigation: "Investigating",
    },
    nexustel: {
        title: "Ask NexusTel",
        subtitle: "Operational assistant in natural language.",
        placeholder: "e.g. How many critical tickets do we have today?",
        ask: "Ask",
        thinking: "Thinking…",
        morningBriefing: "Morning Briefing",
        refreshBriefing: "Refresh",
        briefingLoading: "Preparing your morning briefing…",
        suggestionsLabel: "Suggestions",
        suggestions: [
            "How many critical tickets do we have today?",
            "What revenue is currently at risk?",
            "How many new customers this week?",
            "What is the total B2B pipeline value?",
            "How many escalated conversations are open?",
        ],
    },
    integrations: {
        title: "Integrations Hub",
        subtitle: "Connect Angola Telecom to any enterprise system.",
        newConnector: "New Connector",
        kpiTotal: "Connectors",
        kpiActive: "Active",
        kpiEvents24h: "Events 24h",
        kpiErrorRate: "Error Rate",
        statuses: {
            active: "Active",
            inactive: "Inactive",
            error: "Error",
            configuring: "Configuring",
        },
        types: {
            rest_api: "REST API",
            webhook: "Webhook",
            sftp: "SFTP",
            oauth2: "OAuth2",
        },
        lastSync: "Last sync",
        events: "events",
        errorRate: "error",
        view: "View",
        test: "Test",
        recentEvents: "Recent Events",
        eventInspector: "Event Inspector",
        mappings: "Field Mappings",
        webhookUrl: "Webhook URL",
        webhookSecret: "Webhook Secret",
        secretWarning: "Save this secret — it won't be shown again.",
        retry: "Retry",
        copySecret: "Copy",
        copied: "Copied!",
        wizardTitle: "New Connector",
        wizardStep1: "Type & Provider",
        wizardStep2: "Authentication",
        wizardStep3: "Field Mapping",
        wizardStep4: "Test",
        wizardStep5: "Activate",
        next: "Next",
        back: "Back",
        save: "Save",
        cancel: "Cancel",
        sourceField: "Source Field",
        targetField: "Target Field",
        transform: "Transform",
        addMapping: "Add Mapping",
        scheduler: "Morning Briefing Cron",
        schedulerNext: "Next run",
        schedulerRunNow: "Run Now",
        empty: "No events for this connector.",
    },
    admin: {
        sectionTitle: "Platform Administration",
        sectionSubtitle: "Users, audit, permissions and settings — Angola Telecom.",
        tabs: {
            users: "Users",
            audit: "Audit",
            rbac: "Permissions (RBAC)",
            settings: "System",
            tenants: "Organisation",
        },
        audit: {
            title: "Audit Log",
            subtitle: "Immutable history of platform events.",
            search: "Search action…",
            allActions: "All actions",
            allResources: "All resources",
            from: "From",
            to: "To",
            export: "Export CSV",
            col_when: "When",
            col_user: "User",
            col_action: "Action",
            col_resource: "Resource",
            col_ip: "IP",
            col_details: "Details",
            empty: "No records match the selected filters.",
            system: "System",
            reset: "Reset filters",
        },
        rbac: {
            title: "Permissions Matrix",
            subtitle: "Module-to-role mapping. Read-only.",
            module: "Module",
            allowed: "Allowed",
            denied: "Denied",
            legendHint: "Grey = denied · Copper = allowed",
        },
        settings: {
            title: "System Settings",
            subtitle: "Features and notification channels.",
            features: "Active features",
            notifications: "Notification Channels",
            channel_email: "Email (SendGrid)",
            channel_inapp: "In-app notifications",
            channel_sms: "SMS",
            sendgridConfigured: "SendGrid configured",
            sendgridMissing: "SendGrid in fallback mode (logs)",
            save: "Save changes",
            saved: "Settings saved.",
            feature: {
                service_ops: "Services & Provisioning",
                field_ops: "Field Operations",
                cx_platform: "CX Platform",
                sales_intelligence: "Sales Intelligence",
                revenue_ops: "Revenue Assurance",
                analytics: "Executive Analytics",
                integrations: "Integrations Hub",
                admin: "Administration",
            },
        },
        tenants: {
            title: "Organisation",
            subtitle: "Tenant details and platform usage.",
            org: "Organisation",
            slug: "Identifier",
            timezone: "Timezone",
            currency: "Currency",
            language: "Language",
            stats: "Usage Statistics",
            stat_users: "Users",
            stat_customers: "Customers",
            stat_orders: "Service Orders",
            stat_tickets: "Tickets",
            stat_connectors: "Connectors",
        },
    },
    offline: {
        banner: "Offline — working from cached data",
        restored: "Connection restored",
    },
    kpiLabels: {
        activations_today: "Activations Today",
        open_tickets: "Open Tickets",
        csat_avg: "Avg. CSAT",
        pipeline_b2b: "B2B Pipeline",
    },
    demoBanner: {
        title: "Demo Environment · Angola Telecom",
        snapshot: "Curated snapshot",
        resetHint: "reset via",
    },
    csat: {
        retentionAlerts: "Retention Alerts (CSAT)",
        alerts: "alerts",
    },
    activityTypes: {
        "user.login": "Sign-in",
        "user.logout": "Sign-out",
        "service.created": "Service created",
        "service.activated": "Service activated",
        "ticket.opened": "Ticket opened",
        "ticket.resolved": "Ticket resolved",
        "integration.synced": "Integration synced",
        "payment.received": "Payment received",
    },
    fieldOps: {
        subtitle: "Intelligent dispatch, real-time map and natural-language search.",
        statuses: {
            por_atribuir: "Unassigned",
            atribuido: "Assigned",
            em_deslocamento: "En route",
            em_progresso: "In progress",
            concluido: "Completed",
            nao_cumprido: "Missed",
        },
        kpiTechsAvailable: "Technicians Available",
        nlqPlaceholder: "Show me available technicians in Talatona…",
        aiAssign: "AI Assign",
        nlqSamples: [
            "Show me available technicians in Talatona",
            "How many critical jobs are unassigned?",
            "Who closed the most tickets this week?",
            "Which jobs are in progress?",
        ],
    },
    idp: {
        fields: {
            bi_number: "ID Number (BI)",
            nif: "Tax ID (NIF)",
            full_name: "Full name",
            gender: "Gender",
            date_of_birth: "Date of birth",
            address: "Address",
            phone: "Phone",
            email: "Email",
        },
        confidenceHigh: "High confidence",
        confidenceMed: "Medium confidence",
        confidenceLow: "Needs review",
        extractionFailed: "Extraction failed.",
        connectionLost: "Connection lost — please try again.",
    },
    askNexustelError: "Couldn't process your question right now.",
    integrationsExtra: {
        namePlaceholder: "Connector name",
        descPlaceholder: "Description (optional)",
        authNone: "No authentication",
        activate: "Activate",
    },
    demoReset: {
        cardTitle: "Reset Demo Data",
        cardDesc: "Wipes and re-creates the entire demo dataset — demo users, customers, orders, tickets, deals, KPIs, connectors. Destructive. Takes up to 90 seconds.",
        button: "Reset Demo",
        running: "Resetting demo data…",
        success: "✓ Demo data reset successfully. Reload the page.",
        error: "✕ Reset failed. Check the backend logs.",
        modalTitle: "Confirm reset",
        modalBody: "This operation wipes all demo data and re-creates it from seeds.",
        modalBodyStrong: "Cannot be undone.",
        modalBodyTail: "Continue?",
        confirm: "Yes, reset now",
    },
    salesExtra: {
        companySample: "Acme Corp",
    },
    convExtra: {
        anonymousCustomer: "Anonymous Customer",
    },
    invoiceModal: {
        title: "Upload Invoice",
        subtitle: "PDF · 10 MB max · structured extraction by Claude",
        dropHint: "Drop your invoice PDF here",
        orClick: "or click to choose a file",
        extracting: "Extracting fields with Claude…",
        uploadDone: "Done.",
        emptyHint: "Drop an invoice PDF on the left to see vendor, totals, line items and tax rate appear here.",
        statusOk: "Clean",
        statusWarning: "Warning",
        statusError: "Error",
        vendorTaxId: "Vendor Tax ID",
        currency: "Currency",
        issueDate: "Issue date",
        dueDate: "Due date",
        subtotal: "Subtotal",
        tax: "Tax",
        total: "Total",
        paymentTerms: "Payment terms",
        vendorAddress: "Vendor address",
        customer: "Customer",
        lineItems: "Line items",
        colDescription: "Description",
        colQty: "Qty",
        colUnit: "Unit price",
        colTotal: "Line total",
        flagsHeader: "Validation",
        createTicket: "Create ticket from invoice",
        openTicket: "View ticket",
        copyJson: "Copy JSON",
        downloadJson: "Download JSON",
        errPickFile: "Pick a PDF first.",
        errOnlyPdf: "Only PDF files are accepted.",
        errTooLarge: "File exceeds the 10 MB limit.",
        errEmpty: "File is empty.",
        errExtract: "Extraction failed.",
        errCreateTicket: "Could not create ticket.",
        trigger: "Upload Invoice",
    },
};

export default en;
